import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import * as Styles from './index.styles';

const { StyledPopover, StyledClose } = Styles;

export const PopOverPositions = {
    BOTTOM_RIGHT: 'BOTTOM_RIGHT',
    BOTTOM_LEFT: 'BOTTOM_LEFT',
    TOP_LEFT: 'TOP_LEFT',
    TOP_RIGHT: 'TOP_RIGHT',
};

export const calcPositions = (bindToElement, ref) => {
    const { top, bottom, left, right } = bindToElement.getBoundingClientRect();

    return {
        [PopOverPositions.BOTTOM_RIGHT]: {
            absolute: {
                left,
                top: bottom,
            },
            relative: {
                left: bindToElement.offsetLeft,
                top: bindToElement.offsetTop + bindToElement.offsetHeight,
            },
        },
        [PopOverPositions.TOP_LEFT]: {
            absolute: {
                left: right - ref.current.offsetWidth,
                top: top - ref.current.offsetHeight,
            },
            relative: {
                left:
                    bindToElement.offsetLeft + bindToElement.offsetWidth - ref.current.offsetWidth,
                top: bindToElement.offsetTop - ref.current.offsetHeight,
            },
        },
        [PopOverPositions.TOP_RIGHT]: {
            absolute: {
                left,
                top: top - ref.current.offsetHeight,
            },
            relative: {
                left: bindToElement.offsetLeft,
                top: bindToElement.offsetTop - ref.current.offsetHeight,
            },
        },
        [PopOverPositions.BOTTOM_LEFT]: {
            absolute: {
                left: right - ref.current.offsetWidth,
                top: bottom,
            },
            relative: {
                left:
                    bindToElement.offsetLeft + bindToElement.offsetWidth - ref.current.offsetWidth,
                top: bindToElement.offsetTop + bindToElement.offsetHeight,
            },
        },
    };
};

export const Popover = ({ bindTo, open, onClose, children, darkMode, ...otherProps }) => {
    const ref = useRef();
    const [visible, setVisible] = useState(open);
    const [position, setPosition] = useState(null);
    const [dimensions, setDimensions] = useState(null);
    const theme = useContext(ThemeContext);

    const onClick = useCallback(
        evt => {
            if (
                ref.current &&
                !ref.current.contains(evt.target) &&
                evt.target.id !== 'input-caledar-icon'
            ) {
                onClose(evt);
                setVisible(false);
            }
        },
        [onClose],
    );

    const calcBestPosition = useCallback(
        bindToElement => {
            const positions = calcPositions(bindToElement, ref, theme);
            let bestPositionKey;

            try {
                bestPositionKey = Object.keys(positions).find(key => {
                    const pos = positions[key];
                    return (
                        pos.absolute.left > 0 &&
                        pos.absolute.left + ref.current.offsetWidth <
                            document.documentElement.clientWidth &&
                        pos.absolute.top > 0 &&
                        pos.absolute.top + ref.current.offsetHeight <
                            document.documentElement.clientHeight
                    );
                });
                if (!bestPositionKey && otherProps.enableMobile) {
                    setDimensions({
                        height: ref.current.offsetHeight,
                        width: ref.current.offsetWidth,
                    });
                }
                bestPositionKey = bestPositionKey || PopOverPositions.BOTTOM_RIGHT;
            } catch (error) {
                bestPositionKey = PopOverPositions.BOTTOM_RIGHT;
            }

            setPosition({ ...positions[bestPositionKey].relative, key: bestPositionKey });
        },
        [theme],
    );

    useEffect(() => {
        document.addEventListener('mousedown', onClick);

        setVisible(false);

        if (ref.current && open) {
            const bindToElement = document.getElementById(bindTo);
            if (bindToElement) {
                setVisible(true);
                calcBestPosition(bindToElement);
            }
        }

        return () => {
            document.removeEventListener('mousedown', onClick);
        };
    }, [open, bindTo, calcBestPosition, onClick, setDimensions]);

    return open ? (
        <StyledPopover
            ref={ref}
            position={position}
            dimensions={dimensions}
            visible={visible}
            darkMode={darkMode}
            {...otherProps}
        >
            {otherProps.showCloseIcon && (
                <StyledClose icon={otherProps.icon || 'xmark'} onClick={evt => onClose(evt)}>
                    close
                </StyledClose>
            )}
            {children}
        </StyledPopover>
    ) : null;
};

Popover.propTypes = {
    bindTo: PropTypes.string.isRequired,
    open: PropTypes.bool,
    enableMobile: PropTypes.bool,
    darkMode: PropTypes.bool,
    showCloseIcon: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.node.isRequired,
    width: PropTypes.string,
};

Popover.defaultProps = {
    open: false,
    enableMobile: false,
    darkMode: false,
    showCloseIcon: false,
    onClose: () => null,
    width: '',
};

Popover.Styles = Styles;
